import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ChevronLeft, ChevronRight, Map as MapIcon } from 'lucide-react';
import { toast } from 'sonner';
import JobHoverPopover from '../schedule/JobHoverPopover';
import CompareTeamCalendar, { colorForTeam, pad, AnyCrewDropZone } from './CompareTeamCalendar';
import CompareChangesSheet from './CompareChangesSheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
const SS_KEY = 'compareEmailEntries';

const sortTeams = (a, b) => {
  const ma = /^PM(\d+)$/.exec(a);
  const mb = /^PM(\d+)$/.exec(b);
  if (ma && mb) return parseInt(ma[1], 10) - parseInt(mb[1], 10);
  if (ma) return -1;
  if (mb) return 1;
  return a.localeCompare(b);
};

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const lastName = (name = '') => {
  const clean = String(name).trim().replace(/\s+(Jr\.?|Sr\.?|II|III|IV)$/i, '');
  const words = clean.split(/\s+/);
  return words[words.length - 1] || clean;
};

const loadEntries = () => {
  try { return JSON.parse(sessionStorage.getItem(SS_KEY) || '[]'); } catch (e) { return []; }
};
const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const collisionDetection = (args) => {
  const pointer = pointerWithin(args);
  const day = pointer.find((c) => String(c.id).startsWith('daycell|'));
  if (day) return [day];
  if (pointer.length > 0) return pointer;
  return rectIntersection(args);
};

const shiftDates = (arr, fromIso, toIso) => {
  const [fy, fm, fd] = fromIso.split('-').map(Number);
  const [ty, tm, td] = toIso.split('-').map(Number);
  const offset = Math.round((new Date(ty, tm - 1, td) - new Date(fy, fm - 1, fd)) / 86400000);
  if (!offset) return arr;
  return arr.map((d) => {
    const [y, m, dd] = d.split('-').map(Number);
    const nd = new Date(y, m - 1, dd + offset);
    return `${nd.getFullYear()}-${pad(nd.getMonth() + 1)}-${pad(nd.getDate())}`;
  });
};

const parseStartHour = (t) => {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2] || '0', 10);
  const ap = (m[3] || '').toLowerCase();
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  if (!ap && h >= 1 && h <= 5) h += 12;
  return h + min / 60;
};

const stopOfTime = (t) => {
  const h = parseStartHour(t);
  if (h === null) return null;
  if (h >= 8 && h <= 10) return '1st';
  if (h >= 13 && h <= 15) return '2nd';
  return null;
};

const CompareTeamsPanel = ({ language = 'pt-BR', onOpenMap }) => {
  const pt = language === 'pt-BR';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const todayKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const [popJobs, setPopJobs] = useState([]);
  const [popPositions, setPopPositions] = useState([]);

  // Mudanças da sessão (só para montar o e-mail — nada é salvo no servidor)
  const [entries, setEntries] = useState(loadEntries);
  const [sheetVisible, setSheetVisible] = useState(() => loadEntries().length > 0);
  const [expandSignal, setExpandSignal] = useState(0);
  const [moveModal, setMoveModal] = useState(null); // {fromTeam, toTeam, date, jobs, checked}
  const [blockBalloon, setBlockBalloon] = useState(null); // {team, date, x, y, existing}
  const [activeDrag, setActiveDrag] = useState(null);

  useEffect(() => {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(entries)); } catch (e) { /* noop */ }
    if (entries.length === 0) setSheetVisible(false);
  }, [entries]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  // Balão com informações do trabalho (igual ao dashboard)
  const handleDayClick = (dayJobs, e) => {
    const clickable = dayJobs.filter((j) =>
      j.has_pdf || ((j.job_type || '').toUpperCase() === 'SERVICE' && j.service_notes)
    );
    if (clickable.length === 0) { setPopJobs([]); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const popW = isMobile ? 280 : 340;
    const positions = clickable.map((job, index) => {
      if (isMobile) {
        return {
          x: Math.max(10, (window.innerWidth - popW) / 2),
          y: Math.min(rect.bottom + 5, window.innerHeight - 300) + index * 300,
        };
      }
      let x = rect.right + 10 + index * (popW + 1);
      let y = rect.top;
      if (x + popW > window.innerWidth) {
        x = Math.max(10, rect.left - popW - 10);
        y = rect.top + index * 300;
      }
      return { x, y };
    });
    const data = clickable.map((j) => {
      if ((j.job_type || '').toUpperCase() === 'SERVICE' && j.service_notes) {
        return { ...j, _service: true };
      }
      return (j.ai_processed && j.ai_pdf_data) ? { ...j, ...j.ai_pdf_data } : { ...j, _processing: true };
    });
    setPopPositions(positions);
    setPopJobs(data);
  };

  useEffect(() => {
    if (popJobs.length === 0 && !blockBalloon) return;
    const onDocClick = (e) => {
      if (!e.target.closest('[data-popover]') && !e.target.closest('button[data-calendar-day]') && !e.target.closest('[data-block-balloon]')) {
        setPopJobs([]);
        setBlockBalloon(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [popJobs.length, blockBalloon]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/schedules`, auth());
        setJobs(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('CompareTeams load error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const moveByJobId = useMemo(() => {
    const m = {};
    entries.forEach((e) => { if (e.type === 'move' && e.job_id) m[e.job_id] = e; });
    return m;
  }, [entries]);

  const blocksByTeamDate = useMemo(() => {
    const m = {};
    entries.forEach((e) => { if (e.type === 'block') m[`${e.team}|${e.date}`] = e; });
    return m;
  }, [entries]);

  const notesByTeamDate = useMemo(() => {
    const m = {};
    entries.forEach((e) => { if (e.type === 'note') m[`${e.team}|${e.date}`] = e; });
    return m;
  }, [entries]);

  const teams = useMemo(() => {
    const s = new Set();
    jobs.forEach((j) => {
      if (j.team) s.add(j.team);
      (j.teams || []).forEach((t) => t && s.add(t));
    });
    entries.forEach((e) => { if (e.to_team && e.to_team !== 'ANY') s.add(e.to_team); });
    return [...s].sort(sortTeams);
  }, [jobs, entries]);

  const jobsByTeamDate = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      const jTeams = new Set([j.team, ...(j.teams || [])].filter(Boolean));
      const mv = moveByJobId[j.id];
      if (mv && mv.to_team !== 'ANY') jTeams.add(mv.to_team); // origem mantida como "película" (ghost)
      const dates = [];
      if (j.dates && j.dates.length) {
        dates.push(...j.dates);
      } else if (j.date) {
        dates.push(j.date);
        const dur = parseInt(j.duration_days) || 1;
        if (dur > 1) {
          const [y, m, d] = j.date.split('-').map(Number);
          for (let k = 1; k < dur; k++) {
            const dt = new Date(y, m - 1, d + k);
            dates.push(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`);
          }
        }
      }
      jTeams.forEach((t) => {
        if (!map[t]) map[t] = {};
        const push = (ds, obj) => ds.forEach((d) => { if (!map[t][d]) map[t][d] = []; map[t][d].push(obj); });
        if (mv && t === mv.from_team) push(dates, { ...j, _ghost: true, _movedTo: mv.to_team, _movedToDate: mv.to_date || null });
        if (mv && t === mv.to_team && mv.to_team !== 'ANY') {
          const provDates = mv.to_date ? shiftDates(dates, j.date, mv.to_date) : dates;
          push(provDates, { ...j, _prov: true });
        }
        if (!mv || (t !== mv.from_team && t !== mv.to_team)) push(dates, j);
      });
    });
    return map;
  }, [jobs, moveByJobId]);

  const monthPrefix = `${ym.y}-${pad(ym.m + 1)}`;
  const countInMonth = (t) => {
    const byDate = jobsByTeamDate[t] || {};
    const ids = new Set();
    Object.keys(byDate).forEach((d) => {
      if (d.startsWith(monthPrefix)) byDate[d].forEach((j) => { if (!j._ghost) ids.add(j.id); });
    });
    return ids.size;
  };

  const toggle = (t) => setSelected((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const prevMonth = () => setYm(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  const nextMonth = () => setYm(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  const monthLabel = `${(pt ? MONTHS_PT : MONTHS_EN)[ym.m]} ${ym.y}`;

  // ===== Drag & Drop =====
  const onDragStart = (ev) => {
    const { team, dateKey } = ev.active.data.current || {};
    const dayJobs = ((jobsByTeamDate[team] || {})[dateKey] || []).filter((j) => !j._ghost);
    setActiveDrag({ team, dateKey, jobs: dayJobs });
    setPopJobs([]);
    setBlockBalloon(null);
  };

  const onDragEnd = (ev) => {
    const drag = activeDrag;
    setActiveDrag(null);
    const { over } = ev;
    if (!over || !drag) return;
    const overId = String(over.id);
    let toTeam; let toDate = null;
    if (overId.startsWith('daycell|')) {
      const parts = overId.split('|');
      toTeam = parts[1];
      toDate = parts[2];
      if (toDate === drag.dateKey) toDate = null;
    } else {
      toTeam = overId.split('|')[1];
    }
    if (!toTeam) return;
    if (toTeam === drag.team && !toDate) return;
    const checked = {};
    drag.jobs.forEach((j) => { checked[j.id] = true; });
    const destDate = toDate || drag.dateKey;
    const dragIds = new Set(drag.jobs.map((j) => j.id));
    const existingJobs = ((jobsByTeamDate[toTeam] || {})[destDate] || []).filter((j) => !j._ghost && !j._prov && !dragIds.has(j.id));
    const currentStop = existingJobs.length ? stopOfTime(existingJobs[0].time) : null;
    setMoveModal({ fromTeam: drag.team, toTeam, date: drag.dateKey, toDate, jobs: drag.jobs, checked, existingJobs, currentStop, stopChoice: null });
  };

  const confirmMove = () => {
    if (!moveModal) return;
    const sel = moveModal.jobs.filter((j) => moveModal.checked[j.id]);
    if (sel.length === 0) { setMoveModal(null); return; }
    let next = [...entries];
    sel.forEach((j) => {
      const existing = moveByJobId[j.id];
      const origFrom = existing ? existing.from_team : moveModal.fromTeam;
      if (existing) next = next.filter((e) => e.id !== existing.id);
      const toDate = moveModal.toDate && moveModal.toDate !== j.date ? moveModal.toDate : null;
      if (origFrom === moveModal.toTeam && !toDate) return; // voltou para a origem = desfaz
      next.push({
        id: newId(),
        type: 'move',
        job_id: j.id,
        date: j.date,
        to_date: toDate,
        from_team: origFrom,
        to_team: moveModal.toTeam,
        customer_last: lastName(j.customer_name),
        city: j.city || '',
        windows: j.windows || 0,
        doors: j.doors || 0,
        job_type: j.job_type || '',
        duration_days: parseInt(j.duration_days) || 1,
        dates: j.dates && j.dates.length ? j.dates : null,
        stop_choice: moveModal.existingJobs?.length ? moveModal.stopChoice : null,
        current_stop: moveModal.existingJobs?.length ? moveModal.currentStop : null,
        created_at: new Date().toISOString(),
      });
    });
    setEntries(next);
    setMoveModal(null);
    // Abre o e-mail automaticamente
    setSheetVisible(true);
    setExpandSignal((s) => s + 1);
  };

  // ===== Block / Anotação (segurar 2s ou botão direito) =====
  const onLongPress = (team, dateKey, x, y) => {
    const bw = 230;
    setBlockBalloon({
      team,
      date: dateKey,
      existingBlock: blocksByTeamDate[`${team}|${dateKey}`],
      existingNote: notesByTeamDate[`${team}|${dateKey}`],
      mode: 'menu',
      noteText: '',
      x: Math.min(Math.max(10, x - bw / 2), window.innerWidth - bw - 10),
      y: Math.max(10, y - 110),
    });
  };

  const confirmBlock = () => {
    if (!blockBalloon) return;
    if (blockBalloon.existingBlock) {
      setEntries(entries.filter((e) => e.id !== blockBalloon.existingBlock.id));
      toast.success(pt ? 'Block removido' : 'Block removed');
    } else {
      setEntries([...entries, { id: newId(), type: 'block', team: blockBalloon.team, date: blockBalloon.date, created_at: new Date().toISOString() }]);
      toast.success(`Block ${blockBalloon.team} ✔`);
      setSheetVisible(true);
      setExpandSignal((s) => s + 1);
    }
    setBlockBalloon(null);
  };

  const saveNote = () => {
    if (!blockBalloon || !blockBalloon.noteText.trim()) return;
    setEntries([...entries, { id: newId(), type: 'note', team: blockBalloon.team, date: blockBalloon.date, text: blockBalloon.noteText.trim(), created_at: new Date().toISOString() }]);
    toast.success(pt ? 'Anotação adicionada!' : 'Note added!');
    setSheetVisible(true);
    setExpandSignal((s) => s + 1);
    setBlockBalloon(null);
  };

  const removeNote = () => {
    if (!blockBalloon?.existingNote) return;
    setEntries(entries.filter((e) => e.id !== blockBalloon.existingNote.id));
    toast.success(pt ? 'Anotação removida' : 'Note removed');
    setBlockBalloon(null);
  };

  const deleteEntry = (entry) => setEntries(entries.filter((e) => e.id !== entry.id));
  const clearAll = () => setEntries([]);
  const closeSheet = () => {
    if (window.confirm(pt ? 'Fechar e limpar todas as mudanças do e-mail?' : 'Close and clear all email changes?')) {
      setEntries([]);
      setSheetVisible(false);
    }
  };

  const fmtDay = (iso) => {
    if (!iso) return '';
    const [, m, d] = iso.split('-');
    return `${m}/${d}`;
  };

  return (
    <div className="space-y-4" data-testid="compare-teams-panel">
      {/* Card 1: seleção de equipes */}
      <div className="rounded-xl border bg-white shadow-sm p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h3 className="font-bold text-slate-800 text-sm">
            👥 {pt ? 'Equipes para comparar' : 'Teams to compare'}
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setSelected(selected.length === teams.length ? [] : [...teams])}
              data-testid="compare-select-all-btn"
            >
              {selected.length === teams.length ? (pt ? 'Limpar' : 'Clear') : (pt ? 'Selecionar todas' : 'Select all')}
            </Button>
            <Button
              size="sm"
              className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={selected.length === 0}
              onClick={() => onOpenMap && onOpenMap([...selected], {
                from: `${ym.y}-${pad(ym.m + 1)}-01`,
                to: `${ym.y}-${pad(ym.m + 1)}-${pad(new Date(ym.y, ym.m + 1, 0).getDate())}`,
              })}
              data-testid="compare-open-map-btn"
            >
              <MapIcon className="h-3.5 w-3.5 mr-1" />
              {pt ? 'Mapa' : 'Map'}{selected.length > 0 ? ` (${selected.length})` : ''}
            </Button>
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-slate-400 py-2">{pt ? 'Carregando equipes...' : 'Loading teams...'}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {teams.map((t) => {
              const active = selected.includes(t);
              const color = colorForTeam(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  data-testid={`compare-team-chip-${t}`}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-colors ${active ? 'text-white shadow-sm' : 'bg-white text-slate-600 hover:border-slate-400'}`}
                  style={active ? { background: color, borderColor: color } : { borderColor: '#e2e8f0' }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: active ? '#fff' : color }} />
                  {t}
                  <span className={`text-[10px] font-normal ${active ? 'text-white/80' : 'text-slate-400'}`}>
                    {countInMonth(t)}
                  </span>
                </button>
              );
            })}
            {teams.length === 0 && (
              <p className="text-sm text-slate-400">{pt ? 'Nenhuma equipe com trabalhos' : 'No teams with jobs'}</p>
            )}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">
          💡 {pt ? 'Arraste um dia com trabalho para outro dia (mesma equipe ou outra) ou para outra equipe (ou para a caixa "Any other crew") para montar o e-mail. Segure um dia por 2s (ou botão direito) para Block ou Anotação.' : 'Drag a day with a job onto another day (same or different team), another team, or the "Any other crew" box to build the email. Hold a day for 2s (or right-click) for Block or Note.'}
        </p>
      </div>

      {/* Navegação de mês */}
      {selected.length > 0 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={prevMonth} className="h-8 w-8 p-0" data-testid="compare-prev-month-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-bold text-slate-800 text-sm min-w-[150px] text-center" data-testid="compare-month-label">
            {monthLabel}
          </span>
          <Button variant="outline" size="sm" onClick={nextMonth} className="h-8 w-8 p-0" data-testid="compare-next-month-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Calendários das equipes selecionadas */}
      {selected.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-8" data-testid="compare-empty-hint">
          {pt ? 'Selecione as equipes acima para comparar os calendários' : 'Select teams above to compare calendars'}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveDrag(null)}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[...selected].sort(sortTeams).map((t) => (
              <CompareTeamCalendar
                key={t}
                team={t}
                year={ym.y}
                month={ym.m}
                jobsByDate={jobsByTeamDate[t] || {}}
                todayKey={todayKey}
                pt={pt}
                onDayClick={handleDayClick}
                blocks={blocksByTeamDate}
                notes={notesByTeamDate}
                onLongPress={onLongPress}
              />
            ))}
          </div>
          {activeDrag && <AnyCrewDropZone pt={pt} />}
          <DragOverlay>
            {activeDrag && (
              <div className="rounded-lg border-2 border-blue-500 bg-white shadow-xl px-2.5 py-1.5 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: colorForTeam(activeDrag.team) }} />
                {activeDrag.team} · {fmtDay(activeDrag.dateKey)}
                {activeDrag.jobs.reduce((s, j) => s + (j.doors || 0), 0) > 0 && <span>🚪{activeDrag.jobs.reduce((s, j) => s + (j.doors || 0), 0)}</span>}
                {activeDrag.jobs.reduce((s, j) => s + (j.windows || 0), 0) > 0 && <span>🪟{activeDrag.jobs.reduce((s, j) => s + (j.windows || 0), 0)}</span>}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Balãozinho de Block/Anotação (segurar 2s / botão direito) */}
      {blockBalloon && (
        <div
          data-block-balloon="true"
          className="fixed z-[99995] bg-white border-2 rounded-xl shadow-2xl p-2.5 w-[230px]"
          style={{ left: blockBalloon.x, top: blockBalloon.y, borderColor: '#94a3b8' }}
          data-testid="compare-block-balloon"
        >
          {blockBalloon.mode === 'menu' ? (
            <>
              <p className="text-xs font-bold text-slate-700 mb-2 text-center">
                {blockBalloon.team} — {fmtDay(blockBalloon.date)}
              </p>
              <div className="space-y-1.5">
                <button
                  type="button"
                  onClick={confirmBlock}
                  className={`w-full text-xs font-bold text-white rounded-lg py-1.5 ${blockBalloon.existingBlock ? 'bg-slate-600 hover:bg-slate-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="block-balloon-confirm-btn"
                >
                  {blockBalloon.existingBlock ? (pt ? '🚫 Remover Block' : '🚫 Remove Block') : `🚫 Block ${blockBalloon.team}`}
                </button>
                {blockBalloon.existingNote ? (
                  <button
                    type="button"
                    onClick={removeNote}
                    className="w-full text-xs font-bold text-white rounded-lg py-1.5 bg-slate-600 hover:bg-slate-700"
                    data-testid="note-balloon-remove-btn"
                  >
                    📝 {pt ? 'Remover anotação' : 'Remove note'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setBlockBalloon({ ...blockBalloon, mode: 'note' })}
                    className="w-full text-xs font-bold text-white rounded-lg py-1.5 bg-blue-600 hover:bg-blue-700"
                    data-testid="note-balloon-add-btn"
                  >
                    📝 {pt ? 'Anotação' : 'Note'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-xs font-bold text-slate-700 mb-1.5 text-center">
                📝 {blockBalloon.team} — {fmtDay(blockBalloon.date)}
              </p>
              <input
                type="text"
                autoFocus
                value={blockBalloon.noteText}
                onChange={(e) => setBlockBalloon({ ...blockBalloon, noteText: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') saveNote(); }}
                placeholder={pt ? "Ex: SERVICE (Don't worry about it...)" : 'Ex: SERVICE (...)'}
                className="w-full border rounded-lg px-2 py-1.5 text-xs mb-1.5"
                data-testid="note-balloon-input"
              />
              <p className="text-[10px] text-slate-400 mb-1.5">{pt ? 'Vai sair como:' : 'Will appear as:'} <b>{blockBalloon.team}: {blockBalloon.noteText || '...'}</b></p>
              <button
                type="button"
                onClick={saveNote}
                disabled={!blockBalloon.noteText.trim()}
                className="w-full text-xs font-bold text-white rounded-lg py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                data-testid="note-balloon-save-btn"
              >
                {pt ? 'Salvar anotação' : 'Save note'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Modal de confirmação de movimentação */}
      <Dialog open={!!moveModal} onOpenChange={(o) => { if (!o) setMoveModal(null); }}>
        <DialogContent className="max-w-md" data-testid="compare-move-modal">
          <DialogHeader>
            <DialogTitle className="text-base">
              {pt ? 'Mover trabalho' : 'Move job'} — {fmtDay(moveModal?.date)}
            </DialogTitle>
          </DialogHeader>
          {moveModal && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm font-bold">
                <span className="px-2.5 py-1 rounded-full text-white" style={{ background: colorForTeam(moveModal.fromTeam) }}>{moveModal.fromTeam}{moveModal.toDate ? ` · ${fmtDay(moveModal.date)}` : ''}</span>
                <span className="text-slate-400">→</span>
                {moveModal.toTeam === 'ANY' ? (
                  <span className="px-2.5 py-1 rounded-full text-white bg-slate-500">❓ Any other crew</span>
                ) : (
                  <span className="px-2.5 py-1 rounded-full text-white" style={{ background: colorForTeam(moveModal.toTeam) }}>{moveModal.toTeam}{moveModal.toDate ? ` · ${fmtDay(moveModal.toDate)}` : ''}</span>
                )}
              </div>
              {moveModal.toDate && (
                <p className="text-center text-xs font-semibold text-amber-600" data-testid="move-modal-date-change">
                  📅 {pt ? 'Mudança de dia:' : 'Day change:'} {fmtDay(moveModal.date)} → {fmtDay(moveModal.toDate)}
                </p>
              )}
              {moveModal.existingJobs?.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-1.5" data-testid="move-modal-stop-section">
                  <p className="text-xs font-semibold text-amber-800">
                    ⚠️ {pt ? 'Já existe trabalho neste dia:' : 'There is already a job on this day:'}{' '}
                    {moveModal.existingJobs.map((j) => `${lastName(j.customer_name) || '?'}${j.time ? ` (${j.time})` : ''}`).join(', ')}
                    {moveModal.currentStop && <span> — {pt ? 'atual no' : 'currently on'} <b>{moveModal.currentStop} stop</b></span>}
                  </p>
                  <p className="text-xs font-bold text-slate-700">{pt ? 'Onde adicionar o novo trabalho?' : 'Where should the new job go?'}</p>
                  <div className="flex gap-2">
                    {['1st', '2nd'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setMoveModal({ ...moveModal, stopChoice: s })}
                        className={`flex-1 text-xs font-bold rounded-lg py-1.5 border transition-colors ${moveModal.stopChoice === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                        data-testid={`stop-choice-${s}`}
                      >
                        {s === '1st' ? (pt ? '1º stop' : '1st stop') : (pt ? '2º stop' : '2nd stop')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {moveModal.jobs.map((j) => (
                  <label key={j.id} className="flex items-start gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50" data-testid={`move-job-option-${j.id}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!moveModal.checked[j.id]}
                      onChange={(e) => setMoveModal({ ...moveModal, checked: { ...moveModal.checked, [j.id]: e.target.checked } })}
                    />
                    <span className="text-xs text-slate-700">
                      <span className="font-semibold">{j.customer_name}</span>{j.city ? ` — ${j.city}` : ''}
                      <span className="block text-slate-500">
                        {(j.doors || 0) > 0 && `🚪 ${j.doors} `}{(j.windows || 0) > 0 && `🪟 ${j.windows} `}
                        {(parseInt(j.duration_days) || 1) > 1 && `· ${j.duration_days} ${pt ? 'dias' : 'days'}`}
                        {j._prov && <span className="text-amber-600 font-semibold"> · {pt ? 'já provisório' : 'already provisional'}</span>}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">
                {pt ? 'A mudança é só visual (nesta aba) e entra no texto do e-mail — nada é alterado na agenda real.' : 'The change is visual only (this tab) and goes into the email text — the real schedule is not changed.'}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setMoveModal(null)} data-testid="move-cancel-btn">
                  {pt ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={confirmMove} disabled={moveModal.existingJobs?.length > 0 && !moveModal.stopChoice} data-testid="move-confirm-btn">
                  {pt ? 'Confirmar' : 'Confirm'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sheet com o texto do e-mail (abre automaticamente ao confirmar) */}
      <CompareChangesSheet
        open={sheetVisible && entries.length > 0}
        onClose={closeSheet}
        entries={entries}
        onDelete={deleteEntry}
        onClearAll={clearAll}
        pt={pt}
        expandSignal={expandSignal}
      />

      {/* Balões de informações do trabalho */}
      {popJobs.map((jd, i) => (
        <JobHoverPopover
          key={`${jd.id}-${i}`}
          job={jd}
          extractedData={jd}
          position={popPositions[i] || { x: 0, y: 0 }}
          onClose={() => setPopJobs([])}
        />
      ))}
    </div>
  );
};

export default CompareTeamsPanel;
